<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import ContactCrmAPI from 'dashboard/api/contactCrm';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import CrmTabSummary from './crm-tabs/CrmTabSummary.vue';
import CrmTabData from './crm-tabs/CrmTabData.vue';
import CrmTabAddresses from './crm-tabs/CrmTabAddresses.vue';
import CrmTabProducts from './crm-tabs/CrmTabProducts.vue';
import CrmTabAppointments from './crm-tabs/CrmTabAppointments.vue';
import CrmTabNotes from './crm-tabs/CrmTabNotes.vue';
import CrmTabTimeline from './crm-tabs/CrmTabTimeline.vue';
import CrmTabDocuments from './crm-tabs/CrmTabDocuments.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: null,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  stageColor: {
    type: String,
    default: '#6366f1',
  },
});

const emit = defineEmits(['close', 'refresh']);
const store = useStore();

// State
const activeTab = ref('conversa');
const isLoading = ref(false);
const isConversationLoading = ref(false);
const isSaving = ref(false);
const crmData = ref(null);
const contactData = ref(null);
const cardHistory = ref([]);
const conversationReady = ref(false);

// Tabs configuration
const tabs = [
  { key: 'conversa', label: 'Conversa', icon: 'chat' },
  { key: 'resumo', label: 'Resumo', icon: 'info' },
  { key: 'dados', label: 'Dados', icon: 'person' },
  { key: 'enderecos', label: 'Endereços', icon: 'location' },
  { key: 'produtos', label: 'Produtos', icon: 'briefcase' },
  { key: 'agendamentos', label: 'Agendamentos', icon: 'calendar' },
  { key: 'notas', label: 'Notas', icon: 'draft' },
  { key: 'timeline', label: 'Timeline', icon: 'calendar-clock' },
  { key: 'documentos', label: 'Documentos', icon: 'document' },
];

// Computed
const contact = computed(() => {
  return (
    props.conversation?.meta?.sender || props.conversation?.contact || null
  );
});

const contactId = computed(() => contact.value?.id || null);
const conversationId = computed(() => props.conversation?.id || null);
const displayId = computed(() => props.conversation?.display_id || null);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);

// Estilo do painel com efeito de cor
const panelStyle = computed(() => {
  const color = props.stageColor || '#6366f1';
  return {
    '--panel-color': color,
    '--panel-color-glow': `${color}30`,
    '--panel-color-light': `${color}15`,
  };
});
const allConversations = computed(() => store.getters.getAllConversations);
const currentChat = computed(() => store.getters.getSelectedChat);
const inboxId = computed(() => props.conversation?.inbox_id || null);

// Helper to check if error is a cancellation
const isCanceledError = error => {
  return (
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message?.includes('canceled')
  );
};

// Load conversation into Vuex store for ConversationBox
const loadConversationToStore = async () => {
  const convDisplayId = displayId.value;
  const convId = conversationId.value;
  const convInboxId = inboxId.value;

  if (!convDisplayId) {
    conversationReady.value = false;
    return;
  }

  isConversationLoading.value = true;
  conversationReady.value = false;

  try {
    // Fetch full conversation from API
    await store.dispatch('getConversation', convDisplayId);

    // Find in store
    let conversation = allConversations.value.find(
      c => c.id === convId || c.display_id === convDisplayId
    );

    // If not found, create from props (APIs expect id = display_id)
    if (!conversation && props.conversation) {
      conversation = {
        ...props.conversation,
        id: convDisplayId,
        internal_id: convId,
        meta: props.conversation.meta || {
          sender: props.conversation.contact || contact.value,
        },
      };
      store.commit('ADD_CONVERSATION', conversation);
    }

    if (conversation) {
      // Set as active chat
      await store.dispatch('setActiveChat', { data: conversation });

      // PERFORMANCE: Load supporting data in PARALLEL instead of sequential
      const parallelPromises = [];

      if (convInboxId) {
        // These don't depend on each other - run in parallel
        parallelPromises.push(
          store.dispatch('inboxAssignableAgents/fetch', [convInboxId]),
          store.dispatch('inboxes/get')
        );
      }

      // Messages and labels also don't depend on inbox data - add to parallel
      parallelPromises.push(
        store
          .dispatch('fetchPreviousMessages', { conversationId: convDisplayId })
          .catch(() => null), // Ignore errors
        store
          .dispatch('conversationLabels/get', convDisplayId)
          .catch(() => null) // Ignore errors
      );

      // Wait for all parallel operations
      await Promise.all(parallelPromises);

      await nextTick();
      conversationReady.value = true;
    } else {
      conversationReady.value = false;
    }
  } catch (error) {
    // Ignore canceled requests (user clicked another card)
    if (!isCanceledError(error)) {
      conversationReady.value = false;
    }
  } finally {
    isConversationLoading.value = false;
  }
};

// Load CRM data for contact
const loadCrmData = async () => {
  if (!contactId.value) return;

  isLoading.value = true;
  try {
    const response = await ContactCrmAPI.getCrmData(contactId.value);
    crmData.value = response.data;
    contactData.value = response.data?.contact || contact.value;
  } catch (error) {
    contactData.value = contact.value;
  } finally {
    isLoading.value = false;
  }
};

// Load card history from funnel mappings
const loadCardHistory = async () => {
  if (!conversationId.value) return;

  try {
    const history = [];
    const mappings = props.conversation?.funnel_mappings || [];

    mappings.forEach(mapping => {
      if (mapping.stage_entered_at) {
        history.push({
          id: `stage-${mapping.id}`,
          type: 'stage_change',
          title: `Movido para "${mapping.stage_name}"`,
          description: 'Card movido para este estágio',
          occurred_at: mapping.stage_entered_at,
          icon: 'arrow-right',
        });
      }
    });

    if (props.conversation?.created_at) {
      history.push({
        id: 'created',
        type: 'created',
        title: 'Card criado',
        description: 'Conversa adicionada ao kanban',
        occurred_at: props.conversation.created_at,
        icon: 'add',
      });
    }

    history.sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));
    cardHistory.value = history;
  } catch (error) {
    cardHistory.value = [];
  }
};

const handleSaveContactData = async data => {
  if (!contactId.value) return;

  isSaving.value = true;
  try {
    await ContactCrmAPI.updateCrmData(contactId.value, data);
    await loadCrmData();
    emit('refresh');
  } finally {
    isSaving.value = false;
  }
};

const handleClose = () => {
  if (conversationId.value) {
    store.dispatch('clearSelectedState');
  }
  emit('close');
};

const handleRefresh = () => {
  loadCrmData();
  loadCardHistory();
  emit('refresh');
};

const openConversationInNewTab = () => {
  if (displayId.value && currentAccountId.value) {
    window.open(
      `/app/accounts/${currentAccountId.value}/conversations/${displayId.value}`,
      '_blank'
    );
  }
};

const formatDate = dateString => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const handleKeyDown = event => {
  if (event.key === 'Escape' && !event.shiftKey && !event.ctrlKey) {
    handleClose();
  }
};

// Debounce timer para evitar múltiplas chamadas
let loadDebounceTimer = null;
const LOAD_DEBOUNCE_DELAY = 150;

// Última conversa carregada para evitar recarregar a mesma
let lastLoadedConversationId = null;

// Função consolidada para carregar dados com debounce
const loadPanelData = async (force = false) => {
  const convId = conversationId.value;

  // Se não tem conversa ou painel está fechado, não carregar
  if (!props.conversation || !props.isOpen) {
    return;
  }

  // Se já carregou essa conversa e não é forçado, pular
  if (!force && convId === lastLoadedConversationId) {
    return;
  }

  // Cancelar timer anterior
  if (loadDebounceTimer) {
    clearTimeout(loadDebounceTimer);
  }

  // Debounce para evitar múltiplas chamadas rápidas
  loadDebounceTimer = setTimeout(async () => {
    lastLoadedConversationId = convId;
    activeTab.value = 'conversa';

    // Carregar dados em paralelo para melhor performance
    await loadConversationToStore();
    loadCrmData();
    loadCardHistory();
  }, LOAD_DEBOUNCE_DELAY);
};

// Watcher único que observa ambos conversation e isOpen
watch(
  [() => props.conversation, () => props.isOpen],
  ([newConv, isOpen], [oldConv]) => {
    if (isOpen && newConv) {
      // Se mudou de conversa, forçar reload
      const conversationChanged = newConv?.id !== oldConv?.id;
      loadPanelData(conversationChanged);
    } else if (!isOpen) {
      // Limpar quando fechar
      lastLoadedConversationId = null;
    }
  },
  { immediate: true }
);

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown);

  // Limpar debounce timer para evitar memory leak
  if (loadDebounceTimer) {
    clearTimeout(loadDebounceTimer);
    loadDebounceTimer = null;
  }

  // Limpar estado
  lastLoadedConversationId = null;

  if (conversationId.value) {
    store.dispatch('clearSelectedState');
  }
});
</script>

<template>
  <div
    v-if="isOpen && conversation"
    class="kanban-side-panel fixed top-0 right-0 h-full w-1/2 min-w-[950px] max-w-[1200px] bg-slate-900 border-l border-slate-700 shadow-2xl z-50 flex flex-col"
    :style="panelStyle"
  >
    <!-- Barra lateral colorida do estágio -->
    <div
      class="absolute left-0 top-0 bottom-0 w-0.5 z-10"
      :style="{ backgroundColor: stageColor }"
    />

    <!-- Efeito de glow na borda esquerda -->
    <div
      class="absolute left-0 top-0 bottom-0 w-32 pointer-events-none z-0"
      :style="{
        background: `linear-gradient(to right, var(--panel-color-light) 0%, transparent 100%)`,
      }"
    />

    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/95 relative z-1"
    >
      <div class="flex items-center gap-3">
        <div class="flex-shrink-0">
          <img
            v-if="contact?.thumbnail || contact?.avatar_url"
            :src="contact.thumbnail || contact.avatar_url"
            :alt="contact.name"
            class="w-11 h-11 rounded-full object-cover ring-2 ring-slate-600"
          />
          <div
            v-else
            class="w-11 h-11 rounded-full bg-woot-500 text-white text-base flex items-center justify-center font-medium ring-2 ring-slate-600"
          >
            {{ contact?.name?.charAt(0) || 'U' }}
          </div>
        </div>
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-slate-100 truncate">
            {{ contact?.name || 'Usuário' }}
          </h3>
          <p class="text-xs text-slate-400 truncate">
            {{ contact?.phone_number || contact?.email || 'Sem contato' }}
          </p>
        </div>
      </div>
      <div class="flex items-center gap-1">
        <button
          class="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          title="Abrir em nova aba"
          @click="openConversationInNewTab"
        >
          <fluent-icon icon="open" size="16" />
        </button>
        <button
          class="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          title="Atualizar"
          @click="handleRefresh"
        >
          <fluent-icon icon="arrow-sync" size="16" />
        </button>
        <button
          class="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
          title="Fechar (ESC)"
          @click="handleClose"
        >
          <fluent-icon icon="dismiss" size="16" />
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div
      class="flex items-center overflow-x-auto border-b border-slate-700 bg-slate-800/50 hide-scrollbar px-2"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="tab-button flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-150 border-b-2 flex-shrink-0"
        :class="
          activeTab === tab.key
            ? 'text-woot-400 border-woot-400 bg-slate-800/80'
            : 'text-slate-400 border-transparent hover:text-slate-200 hover:bg-slate-700/50'
        "
        @click="activeTab = tab.key"
      >
        <fluent-icon :icon="tab.icon" size="14" />
        <span>{{ tab.label }}</span>
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-hidden">
      <div
        v-if="isLoading && activeTab !== 'conversa'"
        class="flex items-center justify-center py-12"
      >
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-woot-500"
        />
      </div>

      <!-- Tab: Conversa -->
      <div v-else-if="activeTab === 'conversa'" class="h-full flex flex-col">
        <div
          v-if="isConversationLoading"
          class="flex-1 flex items-center justify-center"
        >
          <div class="text-center">
            <div
              class="animate-spin rounded-full h-10 w-10 border-b-2 border-woot-500 mx-auto mb-4"
            />
            <p class="text-slate-400 text-sm">Carregando conversa...</p>
          </div>
        </div>

        <ConversationBox
          v-else-if="conversationReady && currentChat && currentChat.id"
          :inbox-id="inboxId"
          :is-contact-panel-open="false"
          :is-on-expanded-layout="false"
          :is-inbox-view="false"
          class="flex-1 h-full"
        />

        <div
          v-else
          class="flex items-center justify-center h-full text-slate-500"
        >
          <div class="text-center">
            <fluent-icon icon="chat" size="48" class="mb-3 opacity-50" />
            <p class="text-sm">Nenhuma conversa selecionada</p>
          </div>
        </div>
      </div>

      <!-- Tab: Resumo -->
      <div
        v-else-if="activeTab === 'resumo'"
        class="p-4 h-full overflow-y-auto"
      >
        <CrmTabSummary
          v-if="crmData && contactId"
          :crm-data="crmData"
          :contact-id="contactId"
          @refresh="handleRefresh"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="info" size="48" class="mb-3 opacity-50" />
          <p>Carregando dados do resumo...</p>
        </div>
      </div>

      <!-- Tab: Dados -->
      <div v-else-if="activeTab === 'dados'" class="p-4 h-full overflow-y-auto">
        <CrmTabData
          v-if="contactData"
          :contact="contactData"
          :is-saving="isSaving"
          @save="handleSaveContactData"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="person" size="48" class="mb-3 opacity-50" />
          <p>Nenhum contato vinculado</p>
        </div>
      </div>

      <!-- Tab: Endereços -->
      <div
        v-else-if="activeTab === 'enderecos'"
        class="p-4 h-full overflow-y-auto"
      >
        <CrmTabAddresses
          v-if="contactId"
          :contact-id="contactId"
          :addresses="crmData?.addresses || []"
          @refresh="handleRefresh"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="location" size="48" class="mb-3 opacity-50" />
          <p>Nenhum contato vinculado</p>
        </div>
      </div>

      <!-- Tab: Produtos -->
      <div
        v-else-if="activeTab === 'produtos'"
        class="p-4 h-full overflow-y-auto"
      >
        <CrmTabProducts
          v-if="conversationId"
          :conversation-id="conversationId"
          :products="
            crmData?.products || conversation?.conversation_products || []
          "
          @refresh="handleRefresh"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="briefcase" size="48" class="mb-3 opacity-50" />
          <p>Nenhuma conversa vinculada</p>
        </div>
      </div>

      <!-- Tab: Agendamentos -->
      <div
        v-else-if="activeTab === 'agendamentos'"
        class="p-4 h-full overflow-y-auto"
      >
        <CrmTabAppointments
          v-if="conversationId"
          :conversation-id="conversationId"
          :appointments="
            crmData?.appointments ||
            conversation?.conversation_appointments ||
            []
          "
          @refresh="handleRefresh"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="calendar" size="48" class="mb-3 opacity-50" />
          <p>Nenhuma conversa vinculada</p>
        </div>
      </div>

      <!-- Tab: Notas -->
      <div v-else-if="activeTab === 'notas'" class="p-4 h-full overflow-y-auto">
        <CrmTabNotes
          v-if="contactId"
          :contact-id="contactId"
          :conversation-id="conversationId"
          :notes="
            crmData?.notes || { contact_notes: [], conversation_notes: [] }
          "
          @refresh="handleRefresh"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="draft" size="48" class="mb-3 opacity-50" />
          <p>Nenhum contato vinculado</p>
        </div>
      </div>

      <!-- Tab: Timeline -->
      <div
        v-else-if="activeTab === 'timeline'"
        class="p-4 h-full overflow-y-auto"
      >
        <CrmTabTimeline
          v-if="contactId"
          :contact-id="contactId"
          :timeline="crmData?.timeline || []"
          @refresh="handleRefresh"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="calendar-clock" size="48" class="mb-3 opacity-50" />
          <p>Nenhum contato vinculado</p>
        </div>
      </div>

      <!-- Tab: Documentos -->
      <div
        v-else-if="activeTab === 'documentos'"
        class="p-4 h-full overflow-y-auto"
      >
        <CrmTabDocuments
          v-if="contactId"
          :contact-id="contactId"
          :documents="crmData?.documents || []"
          @refresh="handleRefresh"
        />
        <div v-else class="text-center py-8 text-slate-500">
          <fluent-icon icon="document" size="48" class="mb-3 opacity-50" />
          <p>Nenhum contato vinculado</p>
        </div>
      </div>

      <!-- Tab: Histórico -->
      <div
        v-else-if="activeTab === 'historico'"
        class="p-4 h-full overflow-y-auto"
      >
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-slate-200">
              Histórico do Card
            </h3>
            <button
              class="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-1"
              @click="loadCardHistory"
            >
              <fluent-icon icon="arrow-sync" size="12" />
              Atualizar
            </button>
          </div>

          <div v-if="cardHistory.length > 0" class="space-y-3">
            <div
              v-for="event in cardHistory"
              :key="event.id"
              class="flex items-start gap-3 p-3 bg-slate-800 rounded-lg"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                :class="{
                  'bg-blue-500/20 text-blue-400': event.type === 'stage_change',
                  'bg-green-500/20 text-green-400': event.type === 'created',
                  'bg-purple-500/20 text-purple-400': event.type === 'updated',
                  'bg-yellow-500/20 text-yellow-400':
                    event.type === 'note_added',
                  'bg-slate-700 text-slate-400': ![
                    'stage_change',
                    'created',
                    'updated',
                    'note_added',
                  ].includes(event.type),
                }"
              >
                <fluent-icon :icon="event.icon || 'arrow-clockwise'" size="14" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-slate-200">
                  {{ event.title }}
                </p>
                <p
                  v-if="event.description"
                  class="text-xs text-slate-400 mt-0.5"
                >
                  {{ event.description }}
                </p>
                <p class="text-xs text-slate-500 mt-1">
                  {{ formatDate(event.occurred_at) }}
                </p>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-12 text-slate-500">
            <fluent-icon icon="arrow-clockwise" size="48" class="mb-3 opacity-50" />
            <p class="text-sm">Nenhuma atividade registrada ainda</p>
            <p class="text-xs mt-2 text-slate-600">
              O histórico mostrará movimentações, atualizações e ações neste
              card
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Backdrop -->
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/50 z-40"
    @click="handleClose"
  />
</template>

<style scoped>
.kanban-side-panel {
  animation: slideIn 0.25s ease-out;
  --panel-color: #6366f1;
  --panel-color-glow: rgba(99, 102, 241, 0.3);
  --panel-color-light: rgba(99, 102, 241, 0.08);
  box-shadow:
    -20px 0 60px -15px var(--panel-color-glow),
    0 0 0 1px var(--panel-color-glow);
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0.8;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.tab-button {
  min-width: fit-content;
  height: 40px;
}

:deep(.conversation-details-wrap),
:deep(.conversation-panel),
:deep(.messages-list),
:deep(.conversation-wrap) {
  height: 100% !important;
}

:deep(.back-button),
:deep(.conversation-header .back-button),
:deep([class*='back-button']),
:deep([class*='BackButton']) {
  display: none !important;
}

:deep(.emoji-dialog),
:deep(.emoji-picker) {
  bottom: 100% !important;
  top: auto !important;
  margin-bottom: 8px !important;
}

:deep(.reply-box .emoji-dialog),
:deep(.reply-box .emoji-picker) {
  position: absolute !important;
  bottom: calc(100% + 10px) !important;
  top: auto !important;
  z-index: 1000 !important;
}

:deep(.modal-container),
:deep(.woot-modal),
:deep(.modal) {
  z-index: 9999 !important;
}

:deep(.modal-mask),
:deep(.modal-backdrop) {
  z-index: 9998 !important;
}
</style>
